import { Component, Injector, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, Task, TaskAgendaReading, TasksService } from 'lib-trend-core';
import { catchError, merge, of, Subject, switchMap, takeUntil, timer } from 'rxjs';
import { ActivityFilters } from '../task.component';

const REFRESH_MS = 60_000;

/** A day of the range: its key, its activities. */
export interface RangeDay {
  day: string;
  date: Date;
  activities: Array<Task>;
}

/**
 * The week and month views (SPEC-2026-10-crm-activities-cadence RF-42): the user's activities
 * grouped by the API's `day` key — an open activity on its scheduled day, a concluded one on its
 * conclusion day, in the company's zone — with the area's filters and search and the count of
 * overdue activities of the same result. Reloads every 60 s and after every own action (RNF-02).
 */
@Component({
  selector: 'app-task-range',
  templateUrl: './task-range.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TaskRangeComponent extends AbstractComponent implements OnInit, OnDestroy {

  @Input() mode: 'week' | 'month' = 'week';

  private currentFilters: ActivityFilters = {};
  private readonly reload$ = new Subject<void>();

  @Input() set filters(value: ActivityFilters) {
    this.currentFilters = value ?? {};
    this.reload$.next();
  }

  /** Any day of the period shown; the period is derived from it. */
  anchor = new Date();
  reading?: TaskAgendaReading;
  days: Array<RangeDay> = [];
  override loading = true;
  failed = false;

  /**
   * The days unfolded in the accordion (design, canvas "Semana"). Today starts open — the first
   * day when today is not in the result — and what the user folds or unfolds is kept across the
   * 60 s reload while those days still exist; a new period starts over.
   */
  openDays = new Set<string>();
  private touchedOpenDays = false;

  constructor(injector: Injector, private readonly tasksService: TasksService) {
    super(injector);
  }

  ngOnInit(): void {
    merge(timer(0, REFRESH_MS), this.reload$)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.tasksService.agenda({ ...this.currentFilters, from: this.fromKey, to: this.toKey }).pipe(catchError(() => of(null)))),
      )
      .subscribe((reading) => {
        this.loading = false;
        this.failed = !reading;
        if (reading) {
          this.reading = reading;
          this.days = this.groupByDay(reading.activities ?? []);
          this.reconcileOpenDays();
        }
      });
  }

  isOpen(day: RangeDay): boolean {
    return this.openDays.has(day.day);
  }

  toggleDay(day: RangeDay): void {
    this.touchedOpenDays = true;
    if (this.openDays.has(day.day)) {
      this.openDays.delete(day.day);
    } else {
      this.openDays.add(day.day);
    }
  }

  /** Drops days that left the result; with nothing open and nothing touched, unfolds today, else the first day. */
  private reconcileOpenDays(): void {
    const keys = new Set(this.days.map((day) => day.day));
    for (const key of [...this.openDays]) {
      if (!keys.has(key)) this.openDays.delete(key);
    }
    if (this.openDays.size > 0 || this.touchedOpenDays || this.days.length === 0) return;
    const today = TaskRangeComponent.dayKey(new Date());
    this.openDays.add(keys.has(today) ? today : this.days[0].day);
  }

  /** A new period starts over: nothing kept open, today (or the first day) unfolds again. */
  private resetOpenDays(): void {
    this.openDays.clear();
    this.touchedOpenDays = false;
  }

  /** Monday of the anchor's week, or the first of its month. */
  get periodStart(): Date {
    const start = new Date(this.anchor);
    start.setHours(0, 0, 0, 0);
    if (this.mode === 'week') {
      const daysSinceMonday = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - daysSinceMonday);
    } else {
      start.setDate(1);
    }
    return start;
  }

  /** Sunday of the anchor's week, or the last day of its month. */
  get periodEnd(): Date {
    const end = new Date(this.periodStart);
    if (this.mode === 'week') {
      end.setDate(end.getDate() + 6);
    } else {
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
    }
    return end;
  }

  get fromKey(): string {
    return TaskRangeComponent.dayKey(this.periodStart);
  }

  get toKey(): string {
    return TaskRangeComponent.dayKey(this.periodEnd);
  }

  previous(): void {
    this.shift(-1);
  }

  next(): void {
    this.shift(1);
  }

  today(): void {
    this.anchor = new Date();
    this.resetOpenDays();
    this.loading = true;
    this.reload$.next();
  }

  onChanged(): void {
    this.reload$.next();
  }

  isToday(day: RangeDay): boolean {
    return day.day === TaskRangeComponent.dayKey(new Date());
  }

  private shift(direction: 1 | -1): void {
    const anchor = new Date(this.anchor);
    if (this.mode === 'week') {
      anchor.setDate(anchor.getDate() + 7 * direction);
    } else {
      anchor.setDate(1);
      anchor.setMonth(anchor.getMonth() + direction);
    }
    this.anchor = anchor;
    this.resetOpenDays();
    this.loading = true;
    this.reload$.next();
  }

  /** Rows arrive flat with their `day`; the view groups them, in day order. */
  private groupByDay(activities: Array<Task>): Array<RangeDay> {
    const byDay = new Map<string, Array<Task>>();
    for (const activity of activities) {
      const key = activity.day ?? '';
      if (!key) continue;
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(activity);
    }
    return [...byDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, list]) => ({ day, date: new Date(`${day}T00:00:00`), activities: list }));
  }

  /** `yyyy-MM-dd` of a local date — the calendar day the user is looking at. */
  static dayKey(date: Date): string {
    return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`;
  }

  override ngOnDestroy(): void {
    this.reload$.complete();
    super.ngOnDestroy();
  }
}
